import { z } from 'zod';
import {
  DEFAULT_THEME,
  THEME_FONTS,
  THEME_MENUS,
  themeFileSchema,
  type ThemeColors,
  type ThemeFile,
} from '../theme.ts';
import { darkPaletteOf, readableOn, styleColors } from './colors.ts';
import {
  finishOptionsSchema,
  finishSpec,
  FINISH_IDS,
  resolvedFinish,
  type FinishOptions,
} from './finishes.ts';
import {
  LAYOUT_IDS,
  layoutOptionsSchema,
  layoutSpec,
  MOBILE_NAVS,
  resolvedLayoutOptions,
} from './layouts.ts';
import { paletteFromLogo, paletteSpec, withOverrides, type PaletteSpec } from './palettes.ts';
import { STYLE_DENSITIES, styleSpec, type StyleId } from './ready.ts';

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/);

/**
 * A escolha do Estúdio de estilos (ADR 0128): layout, paleta e acabamento, cada um pelo id e com
 * os ajustes do cliente. Nunca guarda o tema calculado.
 */
export const studioChoiceSchema = z.object({
  layout: z.object({ id: z.enum(LAYOUT_IDS), options: layoutOptionsSchema.optional() }),
  palette: z.object({
    id: z.string().max(40),
    accent: hex.optional(),
    colors: z
      .object({
        paper: hex.optional(),
        sheet: hex.optional(),
        ink: hex.optional(),
        muted: hex.optional(),
        rule: hex.optional(),
        sidebar: hex.optional(),
        sidebarText: hex.optional(),
        /** Cores de situação (ADR 0133, parte 4): ficam legíveis sobre o painel, nos dois modos. */
        ok: hex.optional(),
        gold: hex.optional(),
        danger: hex.optional(),
        info: hex.optional(),
      })
      .strip()
      .optional(),
    /** As cores tiradas do logo (paleta `logo`); o logo em si fica na Configuração › Plataforma. */
    logoColors: z.array(hex).max(5).optional(),
  }),
  finish: z.object({ id: z.enum(FINISH_IDS), options: finishOptionsSchema.optional() }),
  mobileNav: z.enum(MOBILE_NAVS).optional(),
  darkMode: z.enum(['auto', 'off']).optional(),
});
export type StudioChoice = z.infer<typeof studioChoiceSchema>;

/**
 * A escolha guardada. `style` e os ajustes são o formato do ADR 0126, que toda base entende;
 * `studio` (ADR 0128) vale por cima quando existe. Base antiga ignora o `studio` e cai no
 * estilo pronto mais parecido, sem quebrar.
 */
export const styleChoiceSchema = z.object({
  style: z.string().max(40),
  accent: hex.optional(),
  font: z.enum(THEME_FONTS).optional(),
  menu: z.enum(THEME_MENUS).optional(),
  density: z.enum(STYLE_DENSITIES).optional(),
  studio: studioChoiceSchema.optional(),
});
export type StyleChoice = z.infer<typeof styleChoiceSchema>;

/** A escolha do formato antigo com os padrões do estilo preenchidos (sem o `studio`). */
export function resolvedChoice(choice: StyleChoice): Required<Omit<StyleChoice, 'studio'>> | null {
  const spec = styleSpec(choice.style);
  if (!spec) return null;
  const { studio: _studio, ...rest } = choice;
  return { ...spec.defaults, ...rest } as Required<Omit<StyleChoice, 'studio'>>;
}

/** O raio menor sai do maior: o mesmo par que os estilos prontos usavam (8/6 e 12/8). */
export function radiusPair(md: number): { sm: string; md: string } {
  const sm = md >= 12 ? md - 4 : Math.max(0, md - 2);
  return { sm: `${sm}px`, md: `${md}px` };
}

/** Uma escolha do formato antigo vista como os três eixos do Estúdio. */
export function studioOfLegacy(choice: StyleChoice): StudioChoice | null {
  const c = resolvedChoice(choice);
  const spec = styleSpec(choice.style);
  if (!c || !spec) return null;
  return {
    layout: { id: c.menu === 'top' ? 'top' : 'classic', options: { density: c.density } },
    palette: { id: spec.id, accent: c.accent },
    finish: {
      id: 'sobrio',
      options: {
        font: c.font,
        radius: String(parseInt(spec.radius.md, 10)) as never,
        panel: spec.panel,
      },
    },
  };
}

/** O Estúdio que vale para uma escolha: o `studio` gravado, se os ids existem; senão o antigo. */
export function studioOf(choice: StyleChoice): StudioChoice | null {
  const s = choice.studio;
  if (s && layoutSpec(s.layout.id) && finishSpec(s.finish.id) && paletteOf(s.palette)) return s;
  return studioOfLegacy(choice);
}

function paletteOf(p: StudioChoice['palette']): PaletteSpec | null {
  const base = p.id === 'logo' ? paletteFromLogo(p.logoColors ?? []) : paletteSpec(p.id);
  return base ? withOverrides(base, p.colors) : null;
}

/**
 * O modo escuro de uma escolha (ADR 0128, fase 6). Sem escolha, fica como era antes do estúdio:
 * a paleta do beadev com o escuro de fábrica, as outras com o mesmo visual nos dois modos (a
 * escolha antiga continua igual). `auto` gera o escuro da paleta; `off` desliga.
 */
export function darkModeOf(s: StudioChoice): 'auto' | 'off' {
  return s.darkMode ?? (paletteOf(s.palette)?.base ? 'off' : 'auto');
}

function darkColorsOf(
  palette: PaletteSpec,
  accent: string,
  light: ThemeColors,
  mode: 'auto' | 'off',
): ThemeColors {
  if (mode === 'off' || palette.dark) return light;
  if (!palette.base) return styleColors(palette, accent, 'dark');
  return styleColors(darkPaletteOf(palette), accent, 'dark');
}

const STATUS = ['ok', 'gold', 'danger', 'info'] as const;

/**
 * As cores de situação escolhidas por cima das da paleta. Viram texto e etiqueta, então ficam
 * legíveis sobre o painel (o aviso, que vai misturado com o texto, só fica como veio).
 */
function withStatus(
  c: ThemeColors,
  o: StudioChoice['palette']['colors'],
  dark: boolean,
): ThemeColors {
  if (!o || !STATUS.some((k) => o[k])) return c;
  const next = { ...c };
  for (const k of STATUS) {
    const v = o[k];
    if (!v) continue;
    next[k] = k === 'gold' ? v : readableOn(v, [c.sheet!], dark);
  }
  return next;
}

/** O tema (formato `base-theme`) de uma escolha do Estúdio, ou null se algum id não existe. */
export function studioTheme(s: StudioChoice, name = 'Estilo'): ThemeFile | null {
  const layout = layoutSpec(s.layout.id);
  const palette = paletteOf(s.palette);
  const finish = resolvedFinish(s.finish.id, s.finish.options);
  if (!layout || !palette || !finish) return null;
  const opts = resolvedLayoutOptions(layout.id, s.layout.options);
  const accent = s.palette.accent ?? palette.accent;
  const light = withStatus(styleColors(palette, accent, 'light'), s.palette.colors, false);
  const darkBase = darkColorsOf(palette, accent, light, darkModeOf(s));
  const dark = darkBase === light ? light : withStatus(darkBase, s.palette.colors, true);
  const height = { compact: '34px', comfortable: '40px', spacious: '44px' }[opts.density];
  return {
    ...DEFAULT_THEME,
    name,
    colors: { light, dark },
    radius: radiusPair(parseInt(finish.radius, 10)),
    controlHeight: height,
    style: {
      panel: finish.panel === 'shadow' || finish.panel === 'both' ? 'shadow' : 'border',
      tableHeader: finish.tableHeader,
      pageHeaderRule: finish.headerRule === 'yes',
      badge: finish.badge === 'soft' || finish.badge === 'solid' ? 'soft' : 'outline',
      secondaryButton: finish.btnSecondary === 'tonal' ? 'tonal' : 'outline',
    },
    font: finish.font,
    pageTitle: {
      ...DEFAULT_THEME.pageTitle,
      weight: parseInt(finish.headWeight, 10) as 500 | 600 | 700,
    },
    menu: layout.menu,
  };
}

/**
 * O tema de uma escolha de estilo, ou null se o estilo não existe mais (quem chama cai no tema
 * seguinte da ordem: ADR 0126).
 */
export function styleTheme(choice: StyleChoice, name = 'Estilo'): ThemeFile | null {
  const s = studioOf(choice);
  return s ? studioTheme(s, name) : null;
}

/** A escolha que existe de verdade: o estilo antigo conhecido, ou o `studio` com ids que existem. */
export function isKnownStyle(choice: StyleChoice): boolean {
  return studioOf(choice) !== null;
}

/**
 * O que pode ser gravado: o estilo antigo precisa existir (é o que a base antiga lê) e o `studio`,
 * quando vem, precisa ter todos os ids conhecidos. Ao ler, `isKnownStyle` é mais tolerante.
 */
export function isSavableStyle(choice: StyleChoice): boolean {
  if (!styleSpec(choice.style)) return false;
  return !choice.studio || studioTheme(choice.studio) !== null;
}

/**
 * Grava uma escolha do Estúdio no formato que toda base lê: o `studio` mais o estilo pronto mais
 * parecido nos campos antigos, para a base que ainda não conhece o Estúdio.
 */
export function storedStyleOf(s: StudioChoice): StyleChoice {
  const legacy: StyleId =
    s.palette.id === 'beadev' ||
    s.palette.id === 'clear' ||
    s.palette.id === 'top' ||
    s.palette.id === 'warm' ||
    s.palette.id === 'dark'
      ? s.palette.id
      : paletteOf(s.palette)?.dark
        ? 'dark'
        : 'clear';
  const theme = studioTheme(s);
  const density = resolvedLayoutOptions(s.layout.id, s.layout.options).density;
  return {
    style: legacy,
    accent: theme?.colors.light.accent,
    font: theme?.font,
    menu: theme?.menu,
    density: density === 'compact' ? 'compact' : 'comfortable',
    studio: s,
  };
}

/** O estilo de fábrica gravado no visual.json (`estilo`), ou null. */
export function factoryChoiceOf(visual: { estilo?: unknown }): StyleChoice | null {
  const parsed = styleChoiceSchema.safeParse(visual.estilo);
  return parsed.success && isKnownStyle(parsed.data) ? parsed.data : null;
}

/**
 * O tema de fábrica do sistema, do visual.json: o estilo pelo nome (`estilo`, ADR 0126); sem ele,
 * o tema copiado dos sistemas antigos (`tema`, ADR 0117); sem nenhum, o do beadev.
 */
export function factoryThemeOf(
  visual: { estilo?: unknown; tema?: unknown },
  name: string,
): ThemeFile {
  const choice = factoryChoiceOf(visual);
  const fromStyle = choice ? styleTheme(choice, name) : null;
  if (fromStyle) return fromStyle;
  const copied = visual.tema ? themeFileSchema.safeParse(visual.tema) : null;
  return copied?.success ? copied.data : DEFAULT_THEME;
}

/**
 * O tema que vale agora (ADR 0126): o montado do zero no Guia visual; senão o estilo escolhido na
 * Configuração › Estilo; senão o de fábrica.
 */
export function activeThemeOf(
  settings: { theme: ThemeFile | null; style: StyleChoice | null } | null | undefined,
  factory: ThemeFile,
  name: string,
): ThemeFile {
  if (settings?.theme) return settings.theme;
  const chosen = settings?.style ? styleTheme(settings.style, name) : null;
  return chosen ?? factory;
}

/**
 * O acabamento que vale agora (ADR 0128), na mesma ordem do tema: com tema montado do zero, o
 * Sóbrio (o tema do zero não escolhe acabamento); senão o do estilo escolhido ou o de fábrica.
 * As telas leem daqui o que o tema não carrega: estado vazio, gráfico, cabeçalho de documento.
 */
export function activeFinishOf(
  settings: { theme: ThemeFile | null; style: StyleChoice | null } | null | undefined,
  factory: StyleChoice | null,
): FinishOptions {
  const sobrio = resolvedFinish('sobrio')!;
  // O tema do zero escolhe painel, etiqueta, botão secundário e cabeçalho; o resto fica no Sóbrio.
  if (settings?.theme) {
    const st = settings.theme.style;
    return {
      ...sobrio,
      panel: st.panel,
      badge: st.badge,
      btnSecondary: st.secondaryButton,
      tableHeader: st.tableHeader,
      headerRule: st.pageHeaderRule ? 'yes' : 'no',
    };
  }
  const style = settings?.style ?? factory;
  const studio = style ? studioOf(style) : null;
  return (studio && resolvedFinish(studio.finish.id, studio.finish.options)) ?? sobrio;
}
